import { Component, ElementRef, forwardRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'ui-price',
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PriceComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => PriceComponent), multi: true },
    ],
    styles: [
        `
        .price {
            display: flex;
            flex: 1;
        }
        .high {
            text-align: right;
        }
        .comma {
            padding: 0 .2em;
            align-self: center;
        }
        .low {
            width: 3em;
            padding-left: .3em;
        }
        `,
    ],
    template: `
        <div class="price">
            <span class="input-group">
                <span class="input-group-prepend">
                    <span class="input-group-text">$</span>
                </span>
                <input #highInput class="form-control high" (blur)="onTouched()">
            </span>
            <span class="comma">,</span>
            <input #lowInput class="form-control low" value="000" maxlength="3" (blur)="onTouched()">
        </div>
    `,
})
export class PriceComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

    @ViewChild('highInput', { static: true }) private highInput: ElementRef<HTMLInputElement>;

    @ViewChild('lowInput', { static: true }) private lowInput: ElementRef<HTMLInputElement>;

    private destroyed = new Subject<void>();

    private onChange = (_: any) => {};

    onTouched = () => {};

    constructor(private elementRef: ElementRef) {}

    ngOnInit() {
        const bindEvents = (input: HTMLInputElement, pattern: RegExp) => {
            let lastValue = input.value;
            let lastSelectionStart = input.selectionStart;
            let lastSelectionEnd = input.selectionEnd;

            return merge(
                // store cursor position on each keydown event
                fromEvent(input, 'keydown').pipe(
                    filter(() => {
                        lastSelectionStart = input.selectionStart;
                        lastSelectionEnd = input.selectionEnd;
                        return false;
                    }),
                ),
                // revert any invalid values to last valid state
                fromEvent(input, 'input').pipe(
                    filter(() => {
                        if (pattern.test(input.value)) {
                            lastValue = input.value;
                            return true;
                        }
                        input.value = lastValue;
                        input.selectionStart = lastSelectionStart;
                        input.selectionEnd = lastSelectionEnd;
                        return false;
                    }),
                )
            );
        };

        merge(
            bindEvents(this.highInput.nativeElement, /^[0-9]*$/),
            bindEvents(this.lowInput.nativeElement, /^[0-9]{0,3}$/),
            fromEvent(this.lowInput.nativeElement, 'blur').pipe(
                tap(() => {
                    this.lowInput.nativeElement.value = this.lowInput.nativeElement.value.padStart(3, '0');
                })
            ),
        ).pipe(
            takeUntil(this.destroyed)
        ).subscribe(() => {
            const num = parseInt(this.highInput.nativeElement.value, 10) * 1000 + parseInt(this.lowInput.nativeElement.value, 10);
            this.onChange(isNaN(num) ? null : num);
        });

        // this.formGroup.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
        //     const num = parseInt(value.high, 10) * 1000 + parseInt(value.low, 10);
        //     this.onChange(isNaN(num) ? null : num);
        //     this.onTouched();
        // });
    }

    /**
     * handler for [for] directive
     */
    @HostListener('click', ['$event'])
    onClick(event: Event) {
        if (event.target === this.elementRef.nativeElement) {
            this.highInput.nativeElement.focus();
        }
    }

    validate(_: AbstractControl) {
        // return this.formGroup.invalid ? { price: true } : null;
        return null;
    }

    writeValue(obj: any) {
        if (typeof obj !== 'number' || isNaN(obj)) {
            obj = NaN;
        }
        if (isNaN(obj)) {
            obj = 0;
        }
        if (obj < 1000) {
            this.highInput.nativeElement.value = '';
        } else {
            this.highInput.nativeElement.value = Math.floor(obj / 1000).toString();
        }
        this.lowInput.nativeElement.value = (obj % 1000).toString().padStart(3, '0');
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.highInput.nativeElement.disabled = isDisabled;
        this.lowInput.nativeElement.disabled = isDisabled;
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }
}
